import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { RefreshCw, TrendingUp, Trash2, Plus } from 'lucide-react'
import { useState } from 'react'

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(3)}%`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString()
}

export default function BenchmarkRates() {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newRate, setNewRate] = useState({
    duration: '3M',
    rate: '',
    rate_date: new Date().toISOString().split('T')[0],
  })

  const { data: ratesData, isLoading } = useQuery({
    queryKey: ['benchmark-rates'],
    queryFn: () => api.getBenchmarkRates(),
  })

  const refreshMutation = useMutation({
    mutationFn: () => api.refreshBenchmarkRates(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-rates'] })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: { duration: string; rate: number; rate_date: string }) =>
      api.createBenchmarkRate({ ...data, source: 'manual' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-rates'] })
      setShowAddForm(false)
      setNewRate({ duration: '3M', rate: '', rate_date: new Date().toISOString().split('T')[0] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (rateId: number) => api.deleteBenchmarkRate(rateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-rates'] })
    },
  })

  const handleAddRate = () => {
    const rateDecimal = parseFloat(newRate.rate) / 100
    if (!isNaN(rateDecimal)) {
      createMutation.mutate({
        duration: newRate.duration,
        rate: rateDecimal,
        rate_date: newRate.rate_date,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const rates = ratesData?.rates || []

  // Standard durations in order
  const durationOrder = ['1M', '3M', '6M', '1Y', '2Y', '5Y', '10Y', '30Y']

  // Sort rates by duration order
  const sortedRates = [...rates].sort((a, b) => {
    const aIdx = durationOrder.indexOf(a.duration)
    const bIdx = durationOrder.indexOf(b.duration)
    if (aIdx === -1 && bIdx === -1) return a.duration_days - b.duration_days
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Benchmark Rates</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Rate
          </button>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {refreshMutation.isPending ? 'Refreshing...' : 'Refresh from FRED'}
          </button>
        </div>
      </div>

      {/* Error message for FRED API */}
      {refreshMutation.isError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-2">FRED API Key Required</h3>
          <p className="text-sm text-yellow-800">
            To fetch rates automatically, you need a FRED API key.
            Get one free at{' '}
            <a
              href="https://fred.stlouisfed.org/docs/api/api_key.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              fred.stlouisfed.org
            </a>
            . For now, you can add rates manually.
          </p>
        </div>
      )}

      {/* Add Rate Form */}
      {showAddForm && (
        <div className="card border-2 border-primary-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add Benchmark Rate</h3>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Duration</label>
              <select
                value={newRate.duration}
                onChange={(e) => setNewRate({ ...newRate, duration: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                {durationOrder.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Rate (%)</label>
              <input
                type="number"
                value={newRate.rate}
                onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
                placeholder="e.g., 4.5"
                step="0.01"
                className="px-3 py-2 border border-gray-300 rounded-lg w-24"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Date</label>
              <input
                type="date"
                value={newRate.rate_date}
                onChange={(e) => setNewRate({ ...newRate, rate_date: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              onClick={handleAddRate}
              disabled={!newRate.rate || createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Adding...' : 'Add'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rates Cards */}
      {sortedRates.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {sortedRates.map(rate => (
            <div key={rate.id} className="p-4 bg-gray-50 rounded-lg text-center relative group">
              <p className="text-lg font-bold text-gray-900">{rate.duration}</p>
              <p className="font-mono text-xl text-primary-600">{formatPercent(rate.rate)}</p>
              <p className="text-xs text-gray-500 mt-1">{rate.duration_days} days</p>
              <button
                onClick={() => deleteMutation.mutate(rate.id)}
                className="absolute top-1 right-1 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete rate"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Rates Table */}
      <div className="card overflow-hidden">
        {sortedRates.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedRates.map((rate) => (
                  <tr key={rate.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{rate.duration}</td>
                    <td className="px-4 py-3 text-right text-sm">{rate.duration_days}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatPercent(rate.rate)}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(rate.rate_date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{rate.source || 'Unknown'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(rate.updated_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => deleteMutation.mutate(rate.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Delete rate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No benchmark rates available.</p>
            <p className="text-sm text-gray-400 mb-4">
              Add rates manually or try refreshing from FRED (requires API key).
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn btn-primary"
            >
              Add Rate Manually
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">About Benchmark Rates</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- Benchmark rates are used for Black-Scholes option pricing calculations</li>
          <li>- The rate closest to the option's time to expiration is used</li>
          <li>- Rates can be automatically fetched from FRED (requires free API key)</li>
          <li>- Standard durations: 1M, 3M, 6M, 1Y, 2Y, 5Y, 10Y, 30Y (Treasury rates)</li>
          <li>- Manual entry is available for custom rates or when API is unavailable</li>
        </ul>
      </div>
    </div>
  )
}
